<template>
  <v-card>
    <v-card-title class="pb-1">
      Requests
      <v-spacer></v-spacer>
      <v-btn-toggle v-model="hours" mandatory dense class="mr-3">
        <v-btn small :value="1">1 h</v-btn>
        <v-btn small :value="24">24 h</v-btn>
        <v-btn small :value="168">7 Tage</v-btn>
        <v-btn small :value="720">30 Tage</v-btn>
      </v-btn-toggle>
      <v-btn icon :loading="loading" @click="load()">
        <v-icon>mdi-reload</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-subtitle class="d-flex align-center flex-wrap pb-2">
      <v-checkbox
          v-model="includeLocal"
          dense
          hide-details
          class="mt-0 mr-6 pt-0"
          label="Lokale IPs anzeigen"
      ></v-checkbox>
      <span class="mr-4">
        {{ points.length }} IPs · {{ formatNumber(totalRequests) }} Requests
      </span>
      <v-chip v-if="unresolvedIps" small outlined class="mr-2">
        {{ unresolvedIps }} ohne Geo
      </v-chip>
    </v-card-subtitle>

    <v-alert v-if="error" type="warning" dense text class="mx-4 mb-2">
      {{ error }} — angezeigt bleiben die zuletzt geladenen Daten.
    </v-alert>

    <div ref="map" class="request-map"></div>

    <v-card-text v-if="!points.length && !loading" class="text--secondary">
      Keine Requests im Zeitraum.
    </v-card-text>
  </v-card>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { weblogApi } from '../../services/weblog'

const REFRESH_MS = 30000

// Marker-Farben. Reihenfolge der Prüfung: lokal, dann fehlerlastig, dann normal.
// Startausschnitt der Karte: Deutschland (Süd-West-Ecke bis Nord-Ost-Ecke).
const GERMANY_BOUNDS = [[47.27, 5.87], [55.06, 15.04]]

const COLOR_LOCAL = '#42a5f5'
const COLOR_ERRORS = '#ef5350'
const COLOR_NORMAL = '#66bb6a'
const COLOR_SELECTED_RING = '#263238'

// Ab diesem Anteil an Antworten mit status >= 400 gilt eine IP als fehlerlastig —
// typisch für Scanner, die sich durch nicht existierende Pfade arbeiten.
const ERROR_SHARE_THRESHOLD = 0.5

const RADIUS_MIN = 4
const RADIUS_MAX = 22

export default {
  name: 'RequestMap',
  props: {
    selectedIp: { type: String, default: null },
  },
  data: () => ({
    hours: 24,
    includeLocal: false,
    points: [],
    totalRequests: 0,
    unresolvedIps: 0,
    loading: false,
    error: null,
    requestSeq: 0,
  }),
  watch: {
    hours(value) {
      // Kein Neu-Einpassen beim Fensterwechsel: über 30 Tage liegen Punkte auf
      // allen Kontinenten, ein fitBounds darüber zeigte die ganze Weltkarte und
      // warf den Deutschland-Ausschnitt weg. Der gewählte Kartenausschnitt
      // gehört dem Betrachter — wer weiter rauszoomen will, tut es selbst.
      this.$emit('window', value)
      this.load()
    },
    includeLocal() {
      this.load()
    },
    selectedIp() {
      this.paintSelection()
      // Die Spaltenbreite ändert sich, wenn das Detailpanel auf- oder zugeht.
      // Ohne invalidateSize bleibt Leaflet bei der alten Größe und zeichnet
      // graue Flächen statt Tiles.
      this.$nextTick(() => {
        if (this.map) this.map.invalidateSize()
      })
    },
  },
  mounted() {
    this.initMap()
    this.$emit('window', this.hours)
    this.load()
    this.timer = setInterval(() => {
      if (!document.hidden) this.load({ silent: true })
    }, REFRESH_MS)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeDestroy() {
    clearInterval(this.timer)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    initMap() {
      // zoomSnap: 0.25 statt der ganzen Stufen. Der Kartencontainer ist mit
      // ~1570 × 520 px sehr breit; fitBounds auf Deutschland wird dort von der
      // Höhe begrenzt und landete mit ganzzahligem Snap auf Zoom 5 — das zeigte
      // von England bis Moskau. Mit Vierteln passt der Ausschnitt (~5,75).
      this.map = L.map(this.$refs.map, { worldCopyJump: true, zoomSnap: 0.25 })
      // Startausschnitt ist Deutschland, nicht die Weltkarte: der Großteil des
      // Traffics kommt von hier, und auf Zoomstufe 2 sind die Marker im Heimat-
      // raum ein einziger Klumpen. fitBounds statt setView, damit der Ausschnitt
      // auch bei schmalerem Container (Detailpanel offen) passt.
      this.map.fitBounds(GERMANY_BOUNDS)
      // Esri "Light Gray Canvas" statt der bunten OSM-Standardtiles: hellgrauer
      // Untergrund, damit die farbigen Request-Marker lesbar bleiben.
      // Vorher lag hier CARTO Positron — CARTO stempelt seine schlüssellosen
      // Tiles inzwischen mit "API KEY REQUIRED" ab (HTTP 200, Watermark im PNG),
      // deshalb der Wechsel. Esri liefert ohne Key und ohne Watermark, dafür
      // nur bis Zoom 16 und ohne @2x-Variante.
      // Esri legt die Beschriftung in einen eigenen Layer — Base + Reference.
      const ESRI = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas'
      L.tileLayer(ESRI + '/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 16,
        attribution: '&copy; Esri, HERE, Garmin, &copy; OpenStreetMap-Mitwirkende',
      }).addTo(this.map)
      L.tileLayer(ESRI + '/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 16,
      }).addTo(this.map)
      this.markerLayer = L.layerGroup().addTo(this.map)
      this.markers = {}
    },
    onVisibilityChange() {
      if (!document.hidden) this.load({ silent: true })
    },
    formatNumber(value) {
      return (value || 0).toLocaleString('de-DE')
    },
    radius(point) {
      return Math.max(RADIUS_MIN, Math.min(RADIUS_MAX, Math.sqrt(point.requests)))
    },
    color(point) {
      if (point.local) return COLOR_LOCAL
      if (point.requests > 0 && point.errors / point.requests > ERROR_SHARE_THRESHOLD) {
        return COLOR_ERRORS
      }
      return COLOR_NORMAL
    },
    // Leaflet setzt Tooltip-Strings per `innerHTML`, escaped also nichts.
    // `city`, `country` und `isp` stammen von ip-api, kommen damit aus
    // WHOIS-/Registry-Daten Dritter, und diese Karte zeigt gerade den Verkehr von
    // Scannern und Bots — genau die Population, die so etwas ausprobiert. Deshalb
    // werden alle Datenwerte escaped; Markup bleiben nur die strukturellen
    // <strong> und <br>, die hier im Code stehen.
    escapeHtml(value) {
      return String(value == null ? '' : value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;')
    },
    tooltip(point) {
      const esc = this.escapeHtml
      const place = [point.city, point.country].filter(Boolean).map(esc).join(', ')
          || 'Ort unbekannt'
      const lines = [
        `<strong>${esc(point.ip)}</strong>`,
        place,
        // requests/errors sind Zahlen aus der Aggregation, formatNumber liefert
        // eine lokalisierte Zahl — kein Fremdtext, kein Escaping nötig.
        `${this.formatNumber(point.requests)} Requests, ${this.formatNumber(point.errors)} Fehler`,
      ]
      if (point.isp) lines.push(esc(point.isp))
      return lines.join('<br>')
    },
    async load({ silent = false } = {}) {
      // Jede Anfrage bekommt eine Nummer. Der 30-s-Intervall-Refresh und ein
      // Fenster-/Filterwechsel können überlappen (24-h-Refresh noch unterwegs,
      // Nutzer klickt "1 h"); trifft eine Antwort ein, deren Nummer nicht mehr
      // die aktuelle ist, wurde sie überholt und wird verworfen — sonst
      // überschreibt die langsamere, ältere Antwort die Karte und die Kopfzeile
      // mit Daten aus dem falschen Fenster, während der Umschalter schon das
      // neue zeigt.
      const seq = ++this.requestSeq
      if (!silent) this.loading = true
      try {
        const data = await weblogApi.map(this.hours, this.includeLocal)
        if (seq !== this.requestSeq) return
        this.points = data.points
        this.totalRequests = data.total_requests
        this.unresolvedIps = data.unresolved_ips
        this.error = null
        this.render()
      } catch (e) {
        if (seq !== this.requestSeq) return
        console.error('[RequestMap]', e)
        // Marker absichtlich stehen lassen: eine leere Karte sieht aus wie
        // "kein Verkehr" und ist damit irreführender als veraltete Punkte.
        this.error = 'Karte konnte nicht geladen werden'
      } finally {
        if (seq === this.requestSeq) this.loading = false
      }
    },
    render() {
      this.markerLayer.clearLayers()
      this.markers = {}
      this.points.forEach((point) => {
        const marker = L.circleMarker([point.lat, point.lon], {
          radius: this.radius(point),
          color: this.color(point),
          fillColor: this.color(point),
          fillOpacity: 0.55,
          weight: 1,
        })
        marker.bindTooltip(this.tooltip(point), { direction: 'top' })
        marker.on('click', () => this.$emit('select', point.ip))
        marker.addTo(this.markerLayer)
        this.markers[point.ip] = { marker, point }
      })
      this.paintSelection()
    },
    paintSelection() {
      Object.keys(this.markers).forEach((ip) => {
        const entry = this.markers[ip]
        const selected = ip === this.selectedIp
        entry.marker.setStyle({
          weight: selected ? 4 : 1,
          color: selected ? COLOR_SELECTED_RING : this.color(entry.point),
        })
      })
    },
  },
}
</script>

<style scoped>
/* Leaflet braucht eine explizite Höhe, sonst rendert der Container 0 px. */
.request-map {
  height: 520px;
  width: 100%;
}

/* #app setzt global text-align: center — ohne diese Umstellung rendern
   Untertitel und Leerzustand-Text dieser Karte zentriert direkt neben dem
   linksbündigen IpDetailPanel.vue. Gleiche Präzedenzfälle wie dort:
   EndpointCard.vue:88, KvTree.vue:64, RobotStatusDialog.vue:110. */
.v-card {
  text-align: left;
}
</style>
